import Dialog from "../../../General/ConfirmableDialog";
import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Typography,
  Paper,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { apiVariable } from "../../../../api/apiVariable";
import useAuth from "../../../../hooks/useAuth";

interface AccidentNotesDialogProps {
  open: boolean;
  onClose: () => void;
  accidentId: string;
}

interface AccidentNote {
  id: string;
  note: string;
  date: string;
  accidentId: string;
}

const AccidentNotesDialog: React.FC<AccidentNotesDialogProps> = ({
  open,
  onClose,
  accidentId,
}) => {
  const [newNote, setNewNote] = useState("");
  const [editingNote, setEditingNote] = useState<AccidentNote | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editNoteText, setEditNoteText] = useState("");

  const { token } = useAuth();
  const queryClient = useQueryClient();

  const { data: notes, isLoading } = useQuery({
    queryKey: ["accidentNotes", accidentId],
    queryFn: async () => {
      const response = await axios.get(
        `${apiVariable}/accidentNotes/byAccident/${accidentId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    enabled: !!accidentId && !!token && open,
  });

  const addNoteMutation = useMutation({
    mutationFn: async (note: string) => {
      const response = await axios.post(
        `${apiVariable}/accidentNote`,
        {
          note,
          accidentId,
          date: new Date().toISOString(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["accidentNotes", accidentId],
      });
      setNewNote("");
    },
  });

  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      await axios.delete(`${apiVariable}/deleteAccidentNote/${noteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["accidentNotes", accidentId],
      });
    },
  });

  const updateNoteMutation = useMutation({
    mutationFn: async ({ noteId, note }: { noteId: string; note: string }) => {
      const response = await axios.put(
        `${apiVariable}/accidentNote/${noteId}`,
        {
          note,
          accidentId,
          date: new Date().toISOString(),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["accidentNotes", accidentId],
      });
      setEditDialogOpen(false);
      setEditingNote(null);
      setEditNoteText("");
    },
  });

  const handleAddNote = () => {
    if (newNote.trim()) {
      addNoteMutation.mutate(newNote);
    }
  };

  const handleDeleteNote = (noteId: string) => {
    deleteNoteMutation.mutate(noteId);
  };

  const handleEditClick = (note: AccidentNote) => {
    setEditingNote(note);
    setEditNoteText(note.note);
    setEditDialogOpen(true);
  };

  const handleEditSave = () => {
    if (editingNote && editNoteText.trim()) {
      updateNoteMutation.mutate({
        noteId: editingNote.id,
        note: editNoteText,
      });
    }
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setEditingNote(null);
    setEditNoteText("");
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Accident Notes</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              placeholder="Add a new note..."
              variant="outlined"
              size="small"
            />
            <Button
              variant="contained"
              onClick={handleAddNote}
              disabled={!newNote.trim() || addNoteMutation.isPending}
            >
              Add Note
            </Button>
          </Box>

          {isLoading ? (
            <Typography>Loading notes...</Typography>
          ) : (
            <List>
              {notes?.map((note: AccidentNote) => (
                <Paper
                  key={note.id}
                  elevation={1}
                  sx={{ mb: 1, p: 1, backgroundColor: "background.default" }}
                >
                  <ListItem>
                    <ListItemText
                      primary={note.note}
                      secondary={new Date(note.date).toLocaleString()}
                    />
                    <ListItemSecondaryAction>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => handleEditClick(note)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteNote(note.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Paper>
              ))}
            </List>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>

      <Dialog
        open={editDialogOpen}
        onClose={handleEditCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Note</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={editNoteText}
            onChange={(e) => setEditNoteText(e.target.value)}
            variant="outlined"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel}>Cancel</Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={!editNoteText.trim() || updateNoteMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AccidentNotesDialog;
